import type { ElementType, ReactNode, Ref } from 'react'

import { useRef } from 'react'
import { useGSAP } from '@gsap/react'

import { gsap } from '@/lib/gsap'

interface RevealProps {
    children: ReactNode
    className?: string
    as?: ElementType
    y?: number
    x?: number
    delay?: number
    duration?: number
    stagger?: number
    start?: string
    once?: boolean
}

export const Reveal = ({
    children,
    className,
    as,
    y = 32,
    x = 0,
    delay = 0,
    duration = 0.95,
    stagger,
    once = true
}: RevealProps) => {
    const Tag = (as ?? 'div') as ElementType
    const ref = useRef<HTMLElement>(null)

    useGSAP(
        () => {
            const el = ref.current
            if (!el) return

            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            const nodes = (stagger != null ? Array.from(el.children) : [el]) as HTMLElement[]

            if (reduce) {
                gsap.set(nodes, { opacity: 1, x: 0, y: 0 })
                return
            }

            // On mobile the layout stacks, so a horizontal (x) reveal slides in from
            // off-screen and leaves empty space on the side. Fall back to a bottom-up
            // reveal there; keep the horizontal motion on desktop.
            const isMobile = window.matchMedia('(max-width: 1023px)').matches
            const fromX = isMobile && x !== 0 ? 0 : x
            const fromY = isMobile && x !== 0 ? y || 40 : y

            // Suppress CSS transitions (e.g. a card's hover `transition-all`) while GSAP
            // drives the reveal — otherwise the transition fights GSAP and the element
            // flickers (fades out on setup, then back in). Restored once the reveal ends.
            nodes.forEach((node) => {
                node.style.transition = 'none'
            })

            gsap.set(nodes, { opacity: 0, y: fromY, x: fromX })

            let played = false
            const play = () => {
                if (played) return
                played = true
                gsap.to(nodes, {
                    opacity: 1,
                    y: 0,
                    x: 0,
                    duration,
                    delay,
                    ease: 'power3.out',
                    stagger: stagger ?? 0,
                    overwrite: true,
                    onComplete: () => {
                        nodes.forEach((node) => {
                            node.style.transition = ''
                        })
                    }
                })
            }

            if (typeof IntersectionObserver === 'undefined') {
                play()
                return
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            play()
                            if (once) observer.disconnect()
                        }
                    })
                },
                { threshold: 0, rootMargin: '0px 0px -12% 0px' }
            )

            observer.observe(el)

            return () => observer.disconnect()
        },
        { scope: ref, dependencies: [] }
    )

    return (
        <Tag ref={ref as Ref<HTMLElement>} className={className}>
            {children}
        </Tag>
    )
}
